import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Checkbox,
  EmptyState,
  Flex,
  Loading,
  Text
} from 'onefaceui-react-components';
import { useDispatch, useSelector } from 'react-redux';

import { actionCreators as actionsPhotoAlbum } from 'react/api/actions/photoAlbum';
import { actionCreators as actionsModals } from 'react/api/actions/modals';
import { checkPermissions, getResourceTo } from 'react/utils/utils';
import { usePhotosAlbum } from 'react/queries/PhotosAlbum';
import { useRoutePhotosAlbums } from 'react/queries/RoutePhotosAlbums';
import { DefaultRequest } from 'scripts/api';
import { downloadSelectedPhotos, hasDeletePermission } from './utils';

import Modal from 'react/components/Modal';
import PhotoAlbumSelectionBar from './PhotoAlbumSelectionBar';
import UploadButton from './UploadButton';

export const PHOTO_ALBUM_MODAL_KEY = 'photoAlbum';

export default function PhotoAlbumModal() {
  const dispatch = useDispatch();

  const { isOpen, data: modalData } = useSelector((state) =>
    state.modals.get(PHOTO_ALBUM_MODAL_KEY).toJS()
  );
  const {
    location: { pathname }
  } = useSelector((state) => state.router);
  const { selectedAlbums } = useSelector((state) => state.photoAlbum.toJS());
  const { routeId, stopId, albumId, entity } = modalData;
  const prevPathnameRef = useRef(pathname);
  const viewerRef = useRef(null);

  const {
    data: photosAlbum,
    isFetching: isFetchingPhotos,
    refetch: refetchPhotos
  } = usePhotosAlbum({
    albumId
  });

  const {
    data: routeAlbums,
    isFetching: isFetchingRoutePhotos,
    refetch: refetchRouteAlbums
  } = useRoutePhotosAlbums({ routeId, stopId, entity });

  const [albums, setAlbums] = useState([]);
  const [currentAlbumId, setCurrentAlbumId] = useState(null);
  const [isLoadingDownloadButton, setIsLoadingDownloadButton] = useState(false);

  const currentAlbum = albums.find((album) => album.id === currentAlbumId);

  const username = currentAlbum?.user?.name;

  const isLoading = isFetchingPhotos || isFetchingRoutePhotos;

  const isAlbumsDisplay = (entity === 'route' || entity === 'stop') && !albumId;

  const isAlbumsEmpty = !isLoading && !albums.length;

  const selectedPhotos = selectedAlbums?.[currentAlbumId] ?? [];

  const isSelectedAll = useMemo(() => {
    const selectedPhotosIDs = Object.values(selectedAlbums)
      .flatMap((album) => album)
      .filter((album) => !!album?.id)
      .map((album) => album.id);

    const pageAlbumsIDs = albums
      ?.flatMap((album) => album.photos)
      .map(({ id }) => id);

    const isAllPhotosSelected = pageAlbumsIDs.every((id) =>
      selectedPhotosIDs.includes(id)
    );

    return selectedPhotosIDs.length > 0 && isAllPhotosSelected;
  }, [selectedAlbums, albums]);

  const selectedPhotosIds = useMemo(
    () => selectedPhotos.map(({ id }) => id),
    [selectedPhotos]
  );

  const showDownloadButton =
    pathname !== '/PhotoAlbum' &&
    Object.values(selectedAlbums).filter(Boolean).length > 0;

  const closeModal = useCallback(() => {
    dispatch(actionsModals.closeModal({ modalKey: PHOTO_ALBUM_MODAL_KEY }));

    if (prevPathnameRef.current !== '/PhotoAlbum') {
      dispatch(actionsPhotoAlbum.clear());
    }

    setAlbums([]);
    setCurrentAlbumId(null);
    viewerRef.current = null;
  }, [dispatch]);

  const getContainer = () => {
    let container = document.querySelector(
      '.mainContainer .reactivo-tabs.gmTabs #photoAlbumModalWrapper'
    );

    if (!container) {
      container = document.createElement('div');
      container.id = 'photoAlbumModalWrapper';

      document
        .querySelector('.mainContainer .reactivo-tabs.gmTabs')
        .appendChild(container);
    }

    return container;
  };

  const handleChangePhotoItem = useCallback(
    ({ isChecked, id }) => {
      if (isChecked && !selectedPhotos.includes(id)) {
        const photo = currentAlbum.photos.find((photo) => photo.id === id);
        if (photo) {
          dispatch(actionsPhotoAlbum.selectPhoto(currentAlbumId, photo));
        }
        return;
      }
      dispatch(actionsPhotoAlbum.deselectPhoto(currentAlbumId, id));
    },
    [albums, currentAlbumId, selectedPhotos]
  );

  const handleChangeSelectAll = (isChecked) => {
    const allAlbumsIds = albums
      .flatMap((item) => item)
      .filter(({ photos }) => photos.length > 0)
      .map(({ id }) => id);

    if (isChecked) {
      dispatch(actionsPhotoAlbum.selectMultipleAlbums(allAlbumsIds));
      return;
    }

    dispatch(actionsPhotoAlbum.deselectAlbums(allAlbumsIds));
  };

  const handleClickDownloadPhotos = () => {
    downloadSelectedPhotos(selectedAlbums, setIsLoadingDownloadButton);
  };

  const requestLargeImage = (event, photoId, viewer) => {
    const { index, originalImage } = event.detail;

    const canvas = event.srcElement.viewer.canvas;
    
    if (!originalImage?.isLargeImg) {
      event.preventDefault();
      canvas.classList.add('viewer-loading');

      Array.from(canvas?.children).forEach((child) => {
        child.style.opacity = 0;
      });

      new DefaultRequest({
        method: 'GET',
        url: `Photo/${photoId}/Image`,
        success: (data) => {
          const base64String = data
            ? `data:image/png;base64,${data.base64Image}`
            : '';
          canvas.classList.remove('viewer-loading');

          originalImage.src = base64String;
          originalImage.isLargeImg = true;

          viewer.update();
          viewer.view(index);
        },
        error: () => {
          canvas.classList.remove('viewer-loading');
        }
      }).send();
    }
  };

  const deletePhoto = (photoId) => {
    new DefaultRequest({
      method: 'DELETE',
      url: `Photo/${photoId}`,
      success: () => fetchAlbums(),
      error: () => {}
    }).send();
  };

  const fetchAlbums = useCallback(() => {
    if (!isAlbumsDisplay) {
      refetchPhotos();
      return;
    }

    refetchRouteAlbums();
  }, [isAlbumsDisplay]);

  const handleClickImage = ({ target }, photoId) => {
    const showDelete = hasDeletePermission();

    // Destroy the existing viewer instance if it exists
    if (viewerRef.current) {
      viewerRef.current.destroy();
      viewerRef.current = null;
    }

    const currentPhotos = currentAlbum?.photos || [];
    const clickedPhotoIndex = currentPhotos.findIndex(
      (photo) => photo.id === photoId
    );

    // Create a container with all album photos for navigation
    const imagesContainer = document.createElement('div');
    imagesContainer.style.display = 'none';
    currentPhotos.forEach(({ id, base64Thumbnail }) => {
      const img = document.createElement('img');
      img.src = `data:image/jpeg;charset=utf-8;base64,${base64Thumbnail}`;
      img.setAttribute('data-photo-id', id);
      imagesContainer.appendChild(img);
    });
    document.body.appendChild(imagesContainer);

    // Create a new viewer instance with all album photos
    const viewer = new Viewer(imagesContainer, {
      view: (event) => {
        const currentImage = event.detail.originalImage;
        const currentPhotoId = currentImage.getAttribute('data-photo-id');
        
        if (currentPhotoId) {
          requestLargeImage(event, parseInt(currentPhotoId, 10), viewer);
        }
      },
      toolbar: {
        reset: { show: true, size: 'large' },
        rotateLeft: { show: true, size: 'large' },
        rotateRight: { show: true, size: 'large' },
        prev: { show: true, size: 'large' },
        next: { show: true, size: 'large' },
        zoomOut: { show: true, size: 'large' },
        zoomIn: { show: true, size: 'large' },
        delete: {
          show: showDelete,
          size: 'large',
          click: () => {
            const currentIndex = viewer.index;
            const originalImage = imagesContainer.children[currentIndex];
            const currentPhotoId = originalImage.getAttribute('data-photo-id');
            
            if (currentPhotoId) {
              deletePhoto(parseInt(currentPhotoId, 10));
              viewer.hide();
            }
          }
        },
        deleteDisabled: { show: !showDelete, size: 'large' }
      },
      hidden: () => {
        imagesContainer.remove();
      }
    });

    viewer.view(clickedPhotoIndex);

    // Store the new viewer instance in the ref
    viewerRef.current = viewer;
  };

  const getPhotoAlbumPermissions = () => {
    const photoAlbumPermissions = ['PHOTOALBUM_CREATE', 'PHOTO_CREATE'];

    return photoAlbumPermissions.every((permission) =>
      checkPermissions(permission)
    );
  };

  const hasPhotoAlbumPermissions =
    getPhotoAlbumPermissions() && isAlbumsDisplay;

  const ModalFooterContainer = (
    <Flex
      className="photo-album-modal-footer"
      alignItems="center"
      justifyContent="space-between"
      gap={12}
      shouldShow={!isLoading && !isAlbumsEmpty}
    >
      <Text size="xs" className="created-by">
        {username && getResourceTo('photoalbum.createdby')}: {username}
      </Text>
      {hasPhotoAlbumPermissions && (
        <UploadButton
          data={modalData}
          albums={albums}
          fetchAlbums={fetchAlbums}
        />
      )}
    </Flex>
  );

  useEffect(() => {
    if (!isAlbumsDisplay && photosAlbum) {
      setCurrentAlbumId(photosAlbum.id);
      setAlbums([photosAlbum]);
    }

    if (isAlbumsDisplay && routeAlbums) {
      const currentAlbums = routeAlbums.map(({ photoAlbum }) => photoAlbum);

      setAlbums(currentAlbums);
      setCurrentAlbumId(currentAlbums?.[0]?.id);
    }
  }, [isAlbumsDisplay, photosAlbum, routeAlbums, isLoading]);

  useEffect(() => {
    const routesToClearState = [
      '/RouteSummary',
      '/RouteSummary/Stops',
      '/PhotoAlbum'
    ];

    const dynamicRoutesPattern = [/^\/maps\/route\/\d+\/details\/stop\/\d+/];

    const isDynamicRoute = dynamicRoutesPattern.some((pattern) =>
      pattern.test(pathname)
    );

    if (routesToClearState.includes(pathname) || isDynamicRoute) {
      if (
        routesToClearState.includes(prevPathnameRef.current) ||
        dynamicRoutesPattern.some((pattern) =>
          pattern.test(prevPathnameRef.current)
        )
      ) {
        if (pathname !== prevPathnameRef.current) {
          dispatch(actionsPhotoAlbum.clear());
        }
      }
    }

    prevPathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    if (isOpen) fetchAlbums();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      viewerRef.current = null;
      return;
    }

    document.querySelector('.viewer-container')?.remove();
  }, [isOpen]);

  const getCategory = (album) => {
    const categoryMapping = {
      lineItem: 'LineItem',
      order: 'Order',
      stop: 'Stop',
      default: 'Route'
    };

    if (album.lineItem) return categoryMapping.lineItem;
    if (album.order) return categoryMapping.order;
    if (album.stop) return categoryMapping.stop;

    return categoryMapping.default;
  };

  useEffect(() => {
    const handleKeyDown = ({ srcElement, key }) => {
      if (
        srcElement.getAttribute('data-testid') === 'photoalbum-item' &&
        key === 'Escape' &&
        isOpen
      ) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeModal]);


  return (
    <Modal
      className={`photoAlbumModal photo-album-modal-${modalData?.id}`}
      isOpen={isOpen}
      title={getResourceTo('gallerypopup.title')}
      closeModalFn={closeModal}
      parentSelector={() => getContainer()}
      footer={ModalFooterContainer}
      shouldCloseOnOverlayClick
      onRequestClose={closeModal}
    >
      <Flex flexDirection="column" shouldShow={!isAlbumsEmpty}>
        {currentAlbum?.photos?.length > 0 && (
          <PhotoAlbumSelectionBar
            onChangeSelectAll={handleChangeSelectAll}
            onClickDownload={handleClickDownloadPhotos}
            albums={selectedAlbums}
            isAllChecked={isSelectedAll}
            showDownloadButton={showDownloadButton}
            showLabelInfo={pathname === '/PhotoAlbum'}
            isLoadingDownloadButton={isLoadingDownloadButton}
          />
        )}
        <Flex
          className="photo-album-modal-content"
          dataTestId="photo-album-modal-content"
        >
          <Flex
            shouldShow={isAlbumsDisplay && !isLoading}
            className="sidebar"
            flexDirection="column"
            gap={12}
            alignItems="center"
            dataTestId="photo-album-modal-sidebar"
          >
            {albums.map((album) => (
              <Flex
                key={album.id}
                className="album-item"
                dataTestId={`${album.id}-album-item`}
                flexDirection="column"
                onClick={() => setCurrentAlbumId(album.id)}
                gap={2}
              >
                <Flex className="album-item-content">
                  {album.photos?.map(({ id, base64Thumbnail }) => (
                    <img
                      key={id}
                      src={`data:image/jpeg;charset=utf-8;base64,${base64Thumbnail}`}
                      alt={`Thumbnail ${id}`}
                    />
                  ))}
                </Flex>
                <Text size="xs">{album.description}</Text>
              </Flex>
            ))}
          </Flex>
          <Flex className="photo-list-container">
            <Flex className="loading-container" shouldShow={isLoading}>
              <Loading message={getResourceTo('loading.message')} isVisible />
            </Flex>
            {albums.map((album) => {
              const resourceCategory = getResourceTo(getCategory(album));

              const creationDate = Monet.Format.Date.formatToLocale(
                album.creationDate
              );
              return (
                <Flex
                  shouldShow={album.id === currentAlbumId && !isLoading}
                  key={album.id}
                  className="album-content"
                  flexDirection="column"
                >
                  <Flex
                    flexDirection="column"
                    className="photo-album-informations"
                  >
                    <Text dataTestId="photo-album-description">
                      {album?.key} -{' '}
                      {album?.description ||
                        getResourceTo('PhotoAlbum.untitled.album')}
                    </Text>
                    <Text size="xs">
                      {Monet.I18n.translateResources(
                        'Gallery.Picture.AlbumCategory',
                        { category: resourceCategory, date: creationDate }
                      )}
                    </Text>
                  </Flex>
                  <div className="photo-list-wrapper">
                    <div className="photo-list" data-testid="photo-list">
                      {album.photos?.map(({ id, base64Thumbnail }) => {
                        const checked = selectedPhotosIds.includes(id);

                        return (
                          <Flex
                            className={`photo-item ${checked ? 'checked' : ''}`}
                            key={id}
                          >
                            <Checkbox
                              dataTestId={`checkbox-item-${id}`}
                              id={id}
                              checked={checked}
                              onChange={(isChecked) =>
                                handleChangePhotoItem({ isChecked, id })
                              }
                              className="oneface-checkbox"
                              tabIndex="0"
                            />
                            <button
                              type="button"
                              onClick={(event) => handleClickImage(event, id)}
                            >
                              <img
                                data-testid={`image-item-${id}`}
                                src={`data:image/jpeg;charset=utf-8;base64,${base64Thumbnail}`}
                                alt={`Thumbnail ${id}`}
                              />
                            </button>
                          </Flex>
                        );
                      })}
                    </div>
                  </div>
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      </Flex>
      <Flex
        className="photos-empty"
        flexDirection="column"
        gap={24}
        alignItems="center"
        justifyContent="center"
        shouldShow={isAlbumsEmpty}
      >
        <EmptyState
          isVisible
          type="photos"
          message={getResourceTo('gallerypopup.nophotos')}
        />
        {hasPhotoAlbumPermissions && (
          <UploadButton
            data={modalData}
            albums={albums}
            fetchAlbums={fetchAlbums}
            type="rounded"
          />
        )}
      </Flex>
    </Modal>
  );
}
